import { BalanceReportItemDetail, BalanceReportItemStateCategory, BalanceReportItemState, BalanceReportItem, BalanceReportItemType, BalanceReportItemActivityType } from '@square/dashboard-protos/models/squareup/balancereporter/model';
import { FormattedDetailVoidedStates, LocalizedFormattedDetailStates } from './types';
import { getImage } from '../from-balance-report-item';
import { momentFromDateTimeWithZone } from '@banking/ui/utils/datetime-converter';
export const MICROSECONDS_PER_MILLISECOND = 1000;

// convert u8a to String
function Uint8ToString(u8a) {
  if (!u8a) {
    return '';
  }
  const bytes = new Uint8Array(u8a);
  const binary = bytes.reduce(function (a, b) {
    return a + String.fromCodePoint(b);
  }, '');
  return binary;
}
export function getStateTranslation(stateCategory) {
  switch (stateCategory) {
    case BalanceReportItemStateCategory.PENDING:
      return 'banking.checking.unifiedActivity.activityState.pending';
    case BalanceReportItemStateCategory.COMPLETED:
      return 'banking.checking.unifiedActivity.activityState.settled';
    default:
      throw new Error(`Cannot handle ${stateCategory} as BalanceReportItem state`);
  }
}
function formatFooter(footerSet) {
  const {
    footer,
    footerLinks
  } = footerSet;
  if (!footer) {
    return undefined;
  }
  if (!footerLinks) {
    return footer;
  }
  const {
    label,
    url,
    token
  } = footerLinks;
  if (!label || !url || !token) {
    return undefined;
  }
  return footer.replace(`{${token}}`, `<a href=${url}>${label}</a>`);
}
function formatTooltip(tooltipSet) {
  const {
    text,
    textLinks
  } = tooltipSet;
  if (!text) {
    return undefined;
  }
  if (!textLinks) {
    return {
      text
    };
  }
  const textLinksArray = [];
  for (const textLink of textLinks) {
    textLinksArray.push({
      label: textLink.label,
      url: textLink.url
    });
  }
  return {
    text,
    textLinks: textLinksArray
  };
}
function formatTransactionsButton(redirectToTransactions) {
  const {
    buttonText,
    paymentToken
  } = redirectToTransactions;
  return {
    buttonText,
    route: 'transaction',
    queryParams: {
      billToken: paymentToken
    }
  };
}
function formatTimestamp(intl, timestampSet, sentry) {
  const {
    timestamp,
    displayFormat,
    translatedLabel
  } = timestampSet;
  if (!timestamp) {
    throw new Error('Timestamp proto must have a timestamp');
  }
  const {
    DisplayFormat
  } = BalanceReportItemDetail.BalanceReportItemDetailLineItem.Timestamp;
  switch (displayFormat) {
    case DisplayFormat.DATE:
      return {
        label: translatedLabel || intl.t('banking.activity.date'),
        value: intl.formatDate(new Date(timestamp.instantUsec / MICROSECONDS_PER_MILLISECOND), {
          format: 'fullDateShort'
        })
      };
    case DisplayFormat.DATE_AND_TIME:
      {
        return {
          label: translatedLabel || intl.t('banking.activity.date-and-time'),
          value: `${intl.formatDate(new Date(timestamp.instantUsec / MICROSECONDS_PER_MILLISECOND), {
            format: 'fullDateShort'
          })}, ${momentFromDateTimeWithZone(timestamp).format('LT')}`
        };
      }
    case DisplayFormat.TIME:
      {
        return {
          label: translatedLabel || intl.t('banking.activity.time'),
          value: momentFromDateTimeWithZone(timestamp).format('LT')
        };
      }
    case DisplayFormat.DATE_AND_TIME_WITH_WEEKDAY:
      {
        return {
          label: translatedLabel || intl.t('banking.activity.date-and-time'),
          value: `${intl.formatDate(new Date(timestamp.instantUsec / MICROSECONDS_PER_MILLISECOND), {
            format: 'fullDateWithDayOfWeek'
          })}, ${momentFromDateTimeWithZone(timestamp).format('LT')}`
        };
      }
    case DisplayFormat.DATE_WITH_WEEKDAY:
      {
        return {
          label: translatedLabel || intl.t('banking.activity.date'),
          value: intl.formatDate(new Date(timestamp.instantUsec / MICROSECONDS_PER_MILLISECOND), {
            format: 'fullDateWithDayOfWeek'
          })
        };
      }
    case DisplayFormat.DO_NOT_USE:
      {
        sentry.captureMessage('Timestamp is a DO_NOT_USE timestamp');
        return undefined;
      }
    default:
      throw new Error(`Cannot handle ${displayFormat} as Timestamp DisplayFormat enum`);
  }
}
function formatBalanceReportItem(item, sentry) {
  const {
    title,
    amount,
    image,
    state,
    type,
    activityType,
    declineDetails
  } = item;
  if (!title) {
    throw new Error('BlanaceReportItem title must not be null or empty');
  }
  if (!amount) {
    throw new Error('BlanaceReportItem amount must not be null');
  }
  if (!state) {
    throw new Error('BalanceReportItem state must not be null');
  }
  return {
    title,
    amount,
    image: image ? getImage(image) : undefined,
    stateCategory: getLocalizedActivityState(state, sentry),
    isVoided: FormattedDetailVoidedStates.includes(state),
    isPrintableCheck: type === BalanceReportItemType.BALANCE_REPORT_ITEM_TYPE_PRINTABLE_CHECK && activityType === BalanceReportItemActivityType.CHECK_TRANSFER_OUT,
    authenticationNeeded: declineDetails?.declineReason === BalanceReportItem.DeclineDetails.DeclineReason.IN_APP_AUTHENTICATION_REQUIRED
  };
}
export function formatActivityDetails(detail, intl, sentry) {
  const {
    attachments,
    footer,
    pillDetail,
    lineItems,
    balanceReportItem,
    disputeStatus,
    banner,
    description,
    findataTransactionInfo
  } = detail;
  if (!balanceReportItem) {
    throw new Error('BalanceReportItemDetail balanceReportItem must not be null');
  }
  const formattedBalanceReportItem = formatBalanceReportItem(balanceReportItem, sentry);
  const {
    transactionId,
    id
  } = balanceReportItem;
  const transactionToken = transactionId || null;
  const itemId = id || null;
  const formattedLineItems = [];
  lineItems.forEach(item => {
    if (!item) {
      throw new Error('BalanceReportItemDetail LineItem proto must not be null');
    }
    const formattedSubtextTimestamp = item.subtext?.timestamp ? formatTimestamp(intl, item.subtext.timestamp, sentry) : undefined;
    const formattedSubtext = formattedSubtextTimestamp && item.subtext?.timestamp?.translatedLabel ? `${item.subtext.timestamp.translatedLabel} ${formattedSubtextTimestamp.value}` : formattedSubtextTimestamp?.value;
    if (item.timestamp) {
      formattedLineItems.push({
        timestamp: formatTimestamp(intl, item.timestamp, sentry),
        formattedSubtext,
        linkAction: item.linkAction
      });
      return;
    }
    if (item.categoryData) {
      if (!transactionId) {
        throw new Error('BalanceReportItemDetail requires a transactionId of balanceReportItem for setting expense type');
      }
      if (!id) {
        throw new Error('BalanceReportItemDetail requires a id of balanceReportItem for setting expense type');
      }
      formattedLineItems.push({
        categoryData: item.categoryData
      });
      return;
    }
    if (!item.description) {
      throw new Error('BalanceReportItemDetail must have a description set if no other fields are set');
    }
    const tooltip = item.description.tooltip ? formatTooltip(item.description.tooltip) : undefined;
    formattedLineItems.push({
      description: {
        label: item.description.translatedLabel,
        value: item.description.translatedValue,
        tooltip
      },
      formattedSubtext,
      linkAction: item.linkAction
    });
  });
  let isPrintableCheck = formattedBalanceReportItem.isPrintableCheck;
  const authenticationNeeded = formattedBalanceReportItem.authenticationNeeded;
  const formattedAttachments = attachments.map(attachment => {
    if (attachment.checkImage && !attachment.checkImage.imageData) {
      throw new Error('Check image attachments must have image data');
    }
    const checkToken = attachment.checkImage?.checkToken;
    const transferToken = attachment.checkImage?.transferToken;
    if (!checkToken || !transferToken) {
      isPrintableCheck = false;
    }
    return {
      checkImage: {
        label: attachment.checkImage?.translatedLabel ?? '',
        image: btoa(Uint8ToString(attachment.checkImage?.imageData)),
        checkToken,
        transferToken
      }
    };
  }) ?? [];

  // Set default displayPosition of banner to TOP
  const defaultBanner = banner ? {
    ...banner,
    displayPosition: banner.displayPosition ?? BalanceReportItemDetail.Banner.DisplayPosition.TOP
  } : banner;
  let result = {
    attachments: formattedAttachments,
    pillDetail,
    transactionToken,
    id: itemId,
    lineItems: formattedLineItems,
    ...formattedBalanceReportItem,
    isDisputable: disputeStatus === BalanceReportItemDetail.DisputeStatus.DISPUTABLE,
    banner: defaultBanner,
    description,
    isPrintableCheck,
    authenticationNeeded,
    findataTransactionInfo
  };
  if (footer) {
    result = {
      ...result,
      detailsFooter: formatFooter(footer)
    };
  }
  if (footer && footer.buttonAction && footer.buttonAction.redirectToTransactions) {
    result = {
      ...result,
      detailsButtonAction: formatTransactionsButton(footer.buttonAction.redirectToTransactions)
    };
  }
  return result;
}
function getLocalizedActivityState(state, sentry) {
  if (state === BalanceReportItemState.DO_NOT_USE_STATE) {
    sentry.captureMessage('Balance Report Item State is a DO_NOT_USE state');
    return '';
  }
  const localizedState = LocalizedFormattedDetailStates[state];
  if (typeof localizedState !== 'string') {
    throw new TypeError(`Cannot handle ${state} as Balance Report Item State enum`);
  }
  return localizedState;
}